import React, { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { cloneDeep, debounce } from "lodash";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Checkbox } from "primereact/checkbox";
import { ConfirmDialog } from "primereact/confirmdialog";
import { DateInput } from "../../components/DateInput/DateInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { Request, RequestShort, RequestType } from "../../models/Request";
import { Product } from "../../models/Product";
import { PartnerType } from "../../models/Partner";
import { RoleType } from "../../models/Role";
import { activeUser } from "../../services/AuthService";
import { NewRepromaterialRequestForm } from "../../forms/NewRepromaterialRequestForm/NewRepromaterialRequestForm";
import { Utility } from "../../core/Utility";
import { getRequestTemplate } from "../../document-templates/RequestTemplate";
import { useApplicationContext } from "../../application/ApplicationContext";
import {
  getAllRequests,
  getOneRequest,
  updateRequestBackofficeStatus,
  deleteOneRequest,
} from "../../services/NewVersionService";
import { getAllProducts } from "../../services/ProductService";
import "./NewReproRequestsPage.scss";

export const NewReproRequestsPage = () => {
  const [selectedRequest, setSelectedRequest] = useState<Request>();
  const [showForm, setShowForm] = useState<string>("");

  const { t } = useTranslation();
  const {
    //load and set filters
    state,
    selectedReqDateFromSet,
    selectedReqDateToSet,
  } = useApplicationContext();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const prevBusinessYearRef = useRef<number>(businessYear);
  const yearStart = useMemo(
    () => new Date(businessYear, 0, 1, 0, 0, 1),
    [businessYear],
  );
  const yearEnd = useMemo(
    () => new Date(businessYear, 11, 31, 23, 59, 59),
    [businessYear],
  );

  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    if (businessYear === currentYear) {
      const sevenDaysAgo = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7,
        0,
        0,
        1,
      );
      const from = clampToBusinessYear(startOfDay(sevenDaysAgo));
      const to = clampToBusinessYear(endOfDay(today));
      return { from, to };
    }
    return { from: yearStart, to: yearEnd };
  };

  const safeDate = (v: any): Date | null => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const clampToBusinessYear = (d: Date) =>
    Utility.clampDateToRange(d, yearStart, yearEnd);

  const [dateFrom, setDateFrom] = useState<Date>(() => {
    const g = safeDate(state.selectedReqDateFrom);
    return g ? clampToBusinessYear(g) : getDefaultRangeForBusinessYear().from;
  });

  const [dateTo, setDateTo] = useState<Date>(() => {
    const g = safeDate(state.selectedReqDateTo);
    return g ? clampToBusinessYear(g) : getDefaultRangeForBusinessYear().to;
  });

  const [allReproRequests, setAllReproRequests] = useState<RequestShort[]>([]);
  const [reproRequests, setReproRequests] = useState<RequestShort[]>([]);
  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [requestToDelete, setRequestToDelete] = useState<string>("");

  const [products, setProducts] = useState<Product[]>([]);
  const [processing, setProcessing] = useState(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);

  const [pdfData, setPdfData] = useState<any>();

  type ReproReqFilters = {
    global: { value: string | null; matchMode: FilterMatchMode };
    requestNumberFormatted: {
      value: string | null;
      matchMode: FilterMatchMode;
    };
    partnerName: { value: string | null; matchMode: FilterMatchMode };
    productNames: { value: string | null; matchMode: FilterMatchMode };
    productAmounts: { value: string | null; matchMode: FilterMatchMode };
    place: { value: string | null; matchMode: FilterMatchMode };
    note: { value: string | null; matchMode: FilterMatchMode };
    reproProducts: { value: string | null; matchMode: FilterMatchMode };
    reproAmounts: { value: string | null; matchMode: FilterMatchMode };
    type: { value: RequestType | null; matchMode: FilterMatchMode };
    transportCost: { value: boolean | null; matchMode: FilterMatchMode };
    commercialistApproved: {
      value: boolean | null;
      matchMode: FilterMatchMode;
    };
    backofficeApproved: { value: boolean | null; matchMode: FilterMatchMode };
    isRequestFinished: { value: boolean | null; matchMode: FilterMatchMode };
  };
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [filters, setFilters] = useState<ReproReqFilters>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    requestNumberFormatted: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    place: { value: null, matchMode: FilterMatchMode.CONTAINS },
    note: { value: null, matchMode: FilterMatchMode.CONTAINS },
    productNames: { value: null, matchMode: FilterMatchMode.CONTAINS },
    productAmounts: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    transportCost: { value: null, matchMode: FilterMatchMode.EQUALS },
    reproProducts: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reproAmounts: { value: null, matchMode: FilterMatchMode.CONTAINS },
    commercialistApproved: {
      value: null as boolean | null,
      matchMode: FilterMatchMode.EQUALS,
    },
    backofficeApproved: {
      value: null as boolean | null,
      matchMode: FilterMatchMode.EQUALS,
    },
    isRequestFinished: {
      value: null as boolean | null,
      matchMode: FilterMatchMode.EQUALS,
    },
  });
  const [commercialistApproved, setCommercialistApproved] =
    useState<boolean>(false);
  const [backofficeApproved, setBackofficeApproved] = useState<boolean>(false);
  const [isRequestFinished, setIsRequestFinished] = useState<boolean>(false);

  const debouncedSetFilters = useMemo(
    () =>
      debounce((nextFilters: ReproReqFilters) => {
        setFilters(nextFilters);
      }, 300),
    [],
  );

  const debouncedSetFilteredReproRequestsRef = useMemo(
    () =>
      debounce((val: RequestShort[]) => {
        filteredReproRequestsRef.current = val;
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
      debouncedSetFilteredReproRequestsRef.cancel();
    };
  }, [debouncedSetFilters, debouncedSetFilteredReproRequestsRef]);

  const toast = useRef(null);
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);
  const tableRef = useRef<any>(null);
  const filteredReproRequestsRef = useRef<RequestShort[] | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const unwrapArray = (resp: any): any[] => {
          const d = resp?.data;
          if (d?.result && Array.isArray(d.result)) return d.result;
          if (Array.isArray(d)) return d;
          return [];
        };

        const resp = await getAllProducts();
        setProducts(unwrapArray(resp) as Product[]);
      } catch (e) {
        console.error(e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Došlo je do greške pri učitavanju šifarnika"),
          life: 3000,
        });
      }
    })();
  }, []);

  const yearStartTs = useMemo(() => yearStart.getTime(), [yearStart]);
  const yearEndTs = useMemo(() => yearEnd.getTime(), [yearEnd]);
  const skipNextGlobalSyncRef = useRef(false);

  const productById = useMemo(() => {
    const m = new Map<string, Product>();
    for (const p of products ?? []) {
      if (p?.id) m.set(String(p.id), p);
    }
    return m;
  }, [products]);

  useEffect(() => {
    const prev = prevBusinessYearRef.current;
    if (prev === businessYear) return;

    prevBusinessYearRef.current = businessYear;

    const { from, to } = getDefaultRangeForBusinessYear();

    skipNextGlobalSyncRef.current = true;

    setDateFrom(from);
    setDateTo(to);
    selectedReqDateFromSet(from);
    selectedReqDateToSet(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessYear, yearStartTs, yearEndTs]);

  useEffect(() => {
    // ako smo upravo promenili businessYear i uradili reset, preskoči jedan sync
    if (skipNextGlobalSyncRef.current) {
      skipNextGlobalSyncRef.current = false;
      return;
    }

    const gFrom = safeDate(state.selectedReqDateFrom);
    const gTo = safeDate(state.selectedReqDateTo);

    const hasGlobal = !!gFrom && !!gTo;
    const globalValidForYear =
      hasGlobal &&
      gFrom! >= yearStart &&
      gFrom! <= yearEnd &&
      gTo! >= yearStart &&
      gTo! <= yearEnd;

    if (globalValidForYear) {
      const fixedFrom = gFrom! > gTo! ? gTo! : gFrom!;
      const fixedTo = gFrom! > gTo! ? gFrom! : gTo!;
      setDateFrom(fixedFrom);
      setDateTo(fixedTo);
      return;
    }

    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedReqDateFromSet(from);
    selectedReqDateToSet(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    yearStartTs,
    yearEndTs,
    state.selectedReqDateFrom,
    state.selectedReqDateTo,
  ]);

  useEffect(() => {
    setProcessing(true);

    const from = startOfDay(dateFrom);
    const to = endOfDay(dateTo);

    getAllRequests(false, from, to)
      .then((response: AxiosResponse) => {
        const allRequests = response.data.result;
        setAllReproRequests(allRequests);
      })
      .catch(() => console.log("error"))
      .finally(() => setProcessing(false));
  }, [refreshData, dateFrom, dateTo]);

  useEffect(() => {
    filteredReproRequestsRef.current = reproRequests;
  }, [reproRequests]);

  const normalizeReproRequests = (requests: any[]) => {
    const allFormatedRequests: any[] = [];

    for (const oneRequest of requests) {
      const requestProducts =
        oneRequest.reproProducts?.slice(0, -1).split(";") ?? [];

      let newProductNames = "";
      for (const oneProductId of requestProducts) {
        newProductNames +=
          (products.find((item) => item.id == oneProductId)?.name ?? "") + ";";
      }
      newProductNames = newProductNames.slice(0, -1);

      const newProductAmounts = oneRequest.reproAmounts?.slice(0, -1) ?? "";
      const newProductNotes = oneRequest.reproNotes?.slice(0, -1) ?? "";

      allFormatedRequests.push({
        ...oneRequest,
        reproProductIds: oneRequest.reproProducts,
        reproProducts: newProductNames === "undefined" ? "" : newProductNames,
        reproAmounts: newProductAmounts,
        reproNotes: newProductNotes,
      });
    }

    return allFormatedRequests as RequestShort[];
  };

  const formatReproRequestsData = (allRequests: RequestShort[]) => {
    let copyOfReproRequests: RequestShort[] = [];

    for (const oneRequest of allRequests) {
      if (
        //privremeno onemoguceno ogranicenje da komercijalista moze videti zahteve za samo njemu dodeljene klijente
        //ogranicenje je u polju partnerCommercialist za svaki zahtev
        // activeUser?.role?.name !== RoleType.Commercialist ||
        // (activeUser?.role?.name === RoleType.Commercialist &&
        //   (activeUser?.firstName + " " + activeUser?.lastName == oneRequest.partnerCommercialist))
        true
      ) {
        copyOfReproRequests.push(oneRequest);
      }
    }
    copyOfReproRequests.sort((a: RequestShort, b: RequestShort) => {
      return a.date < b.date ? 1 : -1;
    });
    setReproRequests(copyOfReproRequests);
    filteredReproRequestsRef.current = copyOfReproRequests;
  };

  useEffect(() => {
    const formatted = normalizeReproRequests(allReproRequests as any[]);
    formatReproRequestsData(formatted);
    resetFilters();
  }, [allReproRequests, products]);

  const resetFilters = () => {
    //reset all other filters
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      requestNumberFormatted: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      place: { value: null, matchMode: FilterMatchMode.CONTAINS },
      note: { value: null, matchMode: FilterMatchMode.CONTAINS },
      productNames: { value: null, matchMode: FilterMatchMode.CONTAINS },
      productAmounts: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.EQUALS },
      transportCost: { value: null, matchMode: FilterMatchMode.EQUALS },
      reproProducts: { value: null, matchMode: FilterMatchMode.CONTAINS },
      reproAmounts: { value: null, matchMode: FilterMatchMode.CONTAINS },
      commercialistApproved: {
        value: null as boolean | null,
        matchMode: FilterMatchMode.EQUALS,
      },
      backofficeApproved: {
        value: null as boolean | null,
        matchMode: FilterMatchMode.EQUALS,
      },
      isRequestFinished: {
        value: null as boolean | null,
        matchMode: FilterMatchMode.EQUALS,
      },
    });
    setGlobalFilterValue("");
    setIsRequestFinished(false);
    setCommercialistApproved(false);
    setBackofficeApproved(false);
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const typeOptions = [
    { label: t("Prijem"), value: RequestType.Import },
    { label: t("Prodaja"), value: RequestType.Export },
    { label: t("Premeštaj"), value: RequestType.Relocation },
  ];

  const transportOptions = [
    { label: t("Da"), value: true },
    { label: t("Ne"), value: false },
  ];

  const typeItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const transportItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const typeRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={typeOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={typeItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const transportRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={transportOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={transportItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const onCommercialistApprovedChange = (e: any) => {
    const value = e.checked ? true : null;
    setFilters((prevFilters) => ({
      ...prevFilters,
      commercialistApproved: {
        value: value,
        matchMode: FilterMatchMode.EQUALS,
      },
    }));
    setCommercialistApproved(e.checked);
  };

  const onBackofficeApprovedChange = (e: any) => {
    const value = e.checked ? true : null;
    setFilters((prevFilters) => ({
      ...prevFilters,
      backofficeApproved: { value: value, matchMode: FilterMatchMode.EQUALS },
    }));
    setBackofficeApproved(e.checked);
  };

  const onRequestFinishedChange = (e: any) => {
    const value = e.checked ? true : null;
    setFilters((prevFilters) => ({
      ...prevFilters,
      isRequestFinished: { value: value, matchMode: FilterMatchMode.EQUALS },
    }));
    setIsRequestFinished(e.checked);
  };

  const commercialistApprovedRowFilterTemplate = () => {
    return (
      <div className="completed-button">
        <Checkbox
          onChange={onCommercialistApprovedChange}
          checked={commercialistApproved}
        />
      </div>
    );
  };

  const backofficeApprovedRowFilterTemplate = () => {
    return (
      <div className="completed-button">
        <Checkbox
          onChange={onBackofficeApprovedChange}
          checked={backofficeApproved}
        />
      </div>
    );
  };

  const isRequestFinishedRowFilterTemplate = () => {
    return (
      <div className="completed-button">
        <Checkbox
          onChange={onRequestFinishedChange}
          checked={isRequestFinished}
        />
      </div>
    );
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    const fallback = getDefaultRangeForBusinessYear().from;
    const raw = value ?? fallback;

    let d = clampToBusinessYear(raw);
    if (d > dateTo) d = dateTo;

    setDateFrom(d);
    selectedReqDateFromSet(d);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    const fallback = getDefaultRangeForBusinessYear().to;
    const raw = value ?? fallback;

    let d = clampToBusinessYear(raw);
    if (d < dateFrom) d = dateFrom;

    setDateTo(d);
    selectedReqDateToSet(d);
  };

  const accept = () => {
    setProcessing(true);

    deleteOneRequest(requestToDelete)
      .then(() => {
        setRequestToDelete("");
        setDeleteConfirmVisible(false);
        setRefreshData((prev) => !prev);

        // @ts-ignore
        toast.current?.show({
          severity: "success",
          detail: t("Zahtev je obrisan"),
          life: 3000,
        });
      })
      .catch((e) => {
        console.log("error", e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Došlo je do greške pri brisanju zahteva"),
          life: 3000,
        });
      })
      .finally(() => setProcessing(false));
  };

  const exportExcel = (request: Request) => {
    const excelObject = [
      { A: "", B: "" },
      { A: "RWA SRBIJA d.o.o.", B: "" },
      { A: "21201 Rumenka", B: "" },
      { A: "Kisački put 3G", B: "" },
      { A: "PIB: 105528683, MB 20405112", B: "" },
      { A: "", B: "" },
      {
        A: "",
        B: `Narudžbenica (broj ${request?.requestNumberFormatted}) - Dispozicija`,
      },
      {
        A: "Datum",
        B: `${Utility.formatDate(new Date(request.date as Date))}`,
      },
      { A: "Naziv kupca", B: `${request.partner?.name}` },
      { A: "Adresa kupca", B: `${request.partner?.address}` },
      { A: "PIB kupca", B: `${request.partner?.vatNumber}` },
      {
        A: "MB kupca",
        B: `${
          request.partner?.type == PartnerType.Company
            ? request.partner.companyNumber
            : request.partner?.personalCodeNumber
        }`,
      },
      { A: "Mesto utovara", B: `${request.place}` },
      { A: "", B: "" },
      { A: "Roba", B: "Količina", C: "Napomena" },
    ];
    JSON.parse(request.repromaterialRequestProductsJSON as string).map(
      (itemProd: any) => {
        excelObject.push({
          A: `${products.find((item) => item.id === itemProd.id)?.name ?? ""}`,
          B: `${itemProd.amount}`,
          C: `${itemProd.note}`,
        });
      },
    );
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(excelBuffer, `Zahtev za rad - ${new Date().getTime()}`);
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  const exportToExcel = async () => {
    try {
      setProcessing(true);

      // uzmi trenutno prikazane zahteve (već su filtrirani/posloženi)
      const base =
        filteredReproRequestsRef.current !== null
          ? filteredReproRequestsRef.current
          : reproRequests;
      const dataToExport = [...base].sort(
        (a, b) =>
          new Date(a.date as any).getTime() - new Date(b.date as any).getTime(),
      );

      const rows = dataToExport.flatMap((req) => {
        const ids: string[] = (req as any)?.reproProductIds
          ? String((req as any).reproProductIds)
              .split(";")
              .filter(Boolean)
          : [];

        const amountsRaw: string[] = req?.reproAmounts
          ? String(req.reproAmounts).split(";").filter(Boolean)
          : [];

        const notesRaw: string[] = (req as any)?.reproNotes
          ? String((req as any).reproNotes)
              .split(";")
              .filter(Boolean)
          : [];

        // jedinica mere iz teksta količina (PG|Kom|KG|TO|L|MVK)
        const unitsFromAmount = amountsRaw.map(
          (a) => a.match(/(PG|Kom|KG|TO|L|MVK)/i)?.[0]?.trim() ?? "",
        );

        // brojčana količina (bez jedinice)
        const amounts = amountsRaw.map((a) => {
          const cleaned = a.replace(/(PG|Kom|KG|TO|L|MVK)/gi, "").trim();
          const num = parseFloat(cleaned.replace(",", "."));
          if (isNaN(num)) return "";
          return Math.round(num * 1000) / 1000;
        });

        return ids.map((productId, i) => {
          const p = productById.get(String(productId));

          return {
            Datum: Utility.formatDate(new Date(req.date as any)),
            Broj: req.requestNumberFormatted ?? "",
            Kupac: req.partnerName ?? "",
            Sifra: p?.code ?? "",
            Naziv: p?.name ?? "",
            Kolicina: amounts[i] ?? "",
            JedinicaMere: p?.measuringUnit ?? unitsFromAmount[i] ?? "",
            Napomena: notesRaw[i] ?? "",
          };
        });
      });

      const xlsx = await import("xlsx");
      const worksheet = xlsx.utils.json_to_sheet(rows);

      // format numeric kolone Kolicina
      if (worksheet["!ref"]) {
        const range = xlsx.utils.decode_range(worksheet["!ref"]);
        const qtyColIndex = 5; // 0:D,1:Broj,2:Kupac,3:Sifra,4:Naziv,5:Kolicina
        for (let r = range.s.r + 1; r <= range.e.r; r++) {
          const addr = xlsx.utils.encode_cell({ r, c: qtyColIndex });
          if (worksheet[addr] && typeof worksheet[addr].v === "number") {
            worksheet[addr].t = "n";
            worksheet[addr].z = Number.isInteger(worksheet[addr].v)
              ? "0"
              : "0.000";
          }
        }
      }
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const fileSaver = await import("file-saver");
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      fileSaver.default.saveAs(
        blob,
        `Zahtevi repromaterijala ${Utility.formatDate(new Date())}.xlsx`,
      );
    } catch (e) {
      console.error("Greška pri eksportu:", e);
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: t("Došlo je do greške pri eksportu podataka"),
        life: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const renderHeader = () => {
    return (
      <div className="search-container">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Globalna pretraga..."
          />
        </span>
      </div>
    );
  };

  const formatDate = (rowData: RequestShort) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const formatType = (rowData: RequestShort) => {
    const type = rowData.type;
    const isDispositions = rowData.isDispositions;
    if (type === RequestType.Import) {
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Prijem")}
        </span>
      );
    }
    if (type === RequestType.Export) {
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Prodaja")}
        </span>
      );
    }
    if (type === RequestType.Relocation) {
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Premeštaj")}
        </span>
      );
    }
  };

  const formatTransportCost = (rowData: RequestShort) => {
    const cost = rowData.transportCost;
    return <span>{cost === true ? t("Da") : t("Ne")}</span>;
  };

  const formatOptions = (rowData: RequestShort) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-plus-square"
          onButtonClick={() => {
            const type = rowData.type;
            const commercialistApproved = rowData.commercialistApproved;
            const backofficeApproved = rowData.backofficeApproved;
            if (
              type == RequestType.Export &&
              !(
                (commercialistApproved === true ||
                  commercialistApproved === null) &&
                (backofficeApproved === true || backofficeApproved === null)
              )
            ) {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Nije moguće kreirati dispozicije za prodaju repromaterijala dok komercijalista i egzekucija ne odobre.`,
                ),
                life: 3000,
              });
            } else {
              const id = rowData.id;
              navigate(`/repro-request-details-fast/${id}`);
            }
          }}
          disabled={
            activeUser?.role?.name === RoleType.Commercialist ||
            activeUser?.role?.name === RoleType.CommercialistII
          }
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            const isDispositions = rowData.isDispositions;
            if (
              isDispositions === true &&
              !(activeUser?.role?.name === RoleType.Superadmin)
            ) {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Nije moguće menjati zahteve za koje postoje već unete dispozicije`,
                ),
                life: 3000,
              });
            } else {
              if (
                rowData.backofficeApproved === true &&
                !(
                  activeUser?.role?.name === RoleType.Administrator ||
                  activeUser?.role?.name === RoleType.Superadmin
                )
              ) {
                //@ts-ignore
                toast.current.show({
                  severity: "error",
                  detail: t(
                    `Nije moguće menjati zahteve koje je egzekucija odobrila`,
                  ),
                  life: 3000,
                });
              } else {
                const id = rowData.id;
                getOneRequest(id).then((resp) => {
                  const request = resp?.data?.result as Request;
                  if (request?.date)
                    request.date = new Date(request.date as any);
                  setSelectedRequest(cloneDeep(request));
                  setShowForm(v4());
                });
              }
            }
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-file-earmark-excel"
          onButtonClick={() => {
            const id = rowData.id;
            getOneRequest(id).then((resp) => {
              const request = resp?.data?.result as Request;
              if (request?.date) request.date = new Date(request.date as any);
              exportExcel(request);
            });
          }}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-printer"
          onButtonClick={() => {
            const id = rowData.id;
            getOneRequest(id)
              .then((resp) => {
                const request = resp?.data?.result as Request;
                if (request?.date) request.date = new Date(request.date as any);
                const newPdf = getRequestTemplate(
                  request,
                  rowData as RequestShort,
                );
                setPdfData(newPdf);
              })
              .then(() => {
                setTimeout(() => {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                }, 700);
              });
          }}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-trash"
          onButtonClick={async () => {
            const isDispositions = rowData.isDispositions;
            if (isDispositions === true) {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Nije moguće brisati zahteve za koje postoje već unete dispozicije`,
                ),
                life: 3000,
              });
            } else {
              const id = rowData.id;
              setRequestToDelete(id);
              setDeleteConfirmVisible(true);
            }
          }}
          disabled={
            activeUser?.role?.name !== RoleType.Administrator &&
            activeUser?.role?.name !== RoleType.Superadmin
          }
        />
      </div>
    );
  };

  const formatIsRequestFinished = (rowData: RequestShort) => {
    return (
      <div className="completed-button">
        <div className="completed-button disabled-checkbox">
          <Checkbox
            id="isRequestFinished"
            checked={rowData.isRequestFinished === true}
            disabled={true}
          />
        </div>
      </div>
    );
  };

  const formatCommercialistApproved = (rowData: RequestShort) => {
    return (
      <div className="completed-button">
        <div className="completed-button disabled-checkbox">
          {rowData.type === RequestType.Export ? (
            <Checkbox
              id="commercialistApproved"
              checked={rowData.commercialistApproved === true}
              disabled={true}
            />
          ) : (
            <></>
          )}
        </div>
      </div>
    );
  };

  const handleBackofficeApprovedChange = async (
    rowData: RequestShort,
    newValue: boolean,
  ) => {
    const updatedRequests = reproRequests.map((request) =>
      request.id === rowData.id
        ? { ...request, backofficeApproved: newValue }
        : request,
    );
    setReproRequests(updatedRequests);

    try {
      setProcessing(true);
      await updateRequestBackofficeStatus(rowData.id, newValue);
      setRefreshData((prev) => !prev);

      //@ts-ignore
      toast.current.show({
        severity: "success",
        detail: `${t("Status egzekucije uspešno izmenjen")}`,
        life: 3000,
      });
    } catch (error) {
      console.error("Greška pri ažuriranju:", error);

      // Rollback u slučaju greške - vratite staru vrednost
      const revertedRequests = reproRequests.map((request) =>
        request.id === rowData.id
          ? { ...request, backofficeApproved: !newValue }
          : request,
      );
      setReproRequests(revertedRequests);

      //@ts-ignore
      toast.current.show({
        severity: "error",
        detail: `${t("Došlo je do greške pri ažuriranju statusa")}`,
        life: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatBackofficeApproved = (rowData: RequestShort) => {
    return (
      <div className="completed-button">
        {rowData.type === RequestType.Export ? (
          <Checkbox
            id={`backofficeApproved-${rowData.id}`}
            checked={rowData.backofficeApproved === true}
            onChange={(e) => handleBackofficeApprovedChange(rowData, e.checked)}
            disabled={
              !(
                activeUser?.role?.name === RoleType.Backoffice ||
                activeUser?.role?.name === RoleType.Logistician ||
                activeUser?.role?.name === RoleType.Administrator ||
                activeUser?.role?.name === RoleType.Superadmin
              ) || processing
            }
          />
        ) : (
          <></>
        )}
      </div>
    );
  };

  const formatProducts = (rowData: RequestShort) => {
    if (!rowData.reproProducts) return null;
    const products = rowData.reproProducts.split(";");
    return <div style={{ whiteSpace: "pre-line" }}>{products.join("\n")}</div>;
  };

  const formatAmounts = (rowData: RequestShort) => {
    if (!rowData.reproAmounts) return null;
    const amounts = rowData.reproAmounts.split(";");
    return <div style={{ whiteSpace: "pre-line" }}>{amounts.join("\n")}</div>;
  };

  const productsFilterFunction = (value: any, filter: any) => {
    if (filter === undefined || filter === null || filter.trim() === "") {
      return true;
    }
    if (value === undefined || value === null) {
      return false;
    }
    return value.reproProducts.toLowerCase().includes(filter.toLowerCase());
  };

  const amountsFilterFunction = (value: any, filter: any) => {
    if (filter === undefined || filter === null || filter.trim() === "") {
      return true;
    }
    if (value === undefined || value === null) {
      return false;
    }
    return value.reproAmounts.toLowerCase().includes(filter.toLowerCase());
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-truck header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">
              {t("Lista kreiranih zahteva repromaterijala")}
            </span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                const { from, to } = getDefaultRangeForBusinessYear();
                //reset local states
                setDateFrom(from);
                setDateTo(to);
                //reset global states
                selectedReqDateFromSet(from);
                selectedReqDateToSet(to);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginRight: "8px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true || products.length === 0}
            />
            <NewRepromaterialRequestForm
              buttonLabel={`${t("Zahtev repromaterijala")}`}
              buttonIcon="bi bi-truck"
              buttonStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
                margin: "0 1rem 0 0",
              }}
              show={showForm}
              refreshingData={() => setRefreshData(!refreshData)}
              request={selectedRequest}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              ref={tableRef}
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              filterDelay={300}
              globalFilterFields={[
                "partnerName",
                "requestNumberFormatted",
                "place",
                "note",
                "reproProducts",
                "reproAmounts",
              ]}
              header={renderHeader()}
              emptyMessage="Nema zahteva repromaterijala za zadate kriterijume"
              value={reproRequests}
              onFilter={(e) => {
                debouncedSetFilters(cloneDeep(e.filters as ReproReqFilters));
              }}
              onValueChange={(val) => {
                debouncedSetFilteredReproRequestsRef(
                  cloneDeep((val ?? []) as RequestShort[]),
                );
              }}
              showGridlines
              stripedRows
            >
              <Column
                body={formatType}
                filter
                filterField={"type"}
                showFilterMenu={false}
                filterElement={typeRowFilterTemplate}
                style={{ minWidth: "150px" }}
                header={t("Tip")}
              />
              <Column
                field="requestNumberFormatted"
                header={t("Broj zahteva")}
                sortable
                filter
                style={{ minWidth: "110px" }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
              />
              <Column
                field="partnerName"
                sortable
                filter
                header={t("Vlasnik robe")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="place"
                filter
                sortable
                header={t("Mesto utovara / istovara")}
                style={{ minWidth: "100px" }}
              />
              {activeUser?.role?.name !== RoleType.Commercialist && (
                <Column
                  field="note"
                  filter
                  header={t("Napomena")}
                  style={{ minWidth: "100px" }}
                />
              )}
              <Column
                body={formatProducts}
                sortable
                filter
                filterField="reproProducts"
                filterMatchMode="custom"
                filterFunction={productsFilterFunction}
                header={t("Naziv robe")}
                style={{ minWidth: "200px" }}
              />
              <Column
                body={formatAmounts}
                sortable
                filter
                filterField="reproAmounts"
                filterMatchMode="custom"
                filterFunction={amountsFilterFunction}
                header={t("Količina")}
                style={{ minWidth: "200px" }}
              />
              <Column
                body={formatTransportCost}
                filter
                filterField={"transportCost"}
                showFilterMenu={false}
                filterElement={transportRowFilterTemplate}
                style={{ minWidth: "130px" }}
                header={t("Trošak prevoza RWA")}
              />
              <Column
                field="commercialistApproved"
                body={formatCommercialistApproved}
                filter
                filterElement={commercialistApprovedRowFilterTemplate}
                header={t("Komercijalista odobrio")}
                style={{ minWidth: "50px" }}
              />
              <Column
                field="backofficeApproved"
                body={formatBackofficeApproved}
                filter
                filterElement={backofficeApprovedRowFilterTemplate}
                header={t("Egzekucija odobrila")}
                style={{ minWidth: "50px" }}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "200px" }}
              />
              <Column
                field="isRequestFinished"
                body={formatIsRequestFinished}
                filter
                filterElement={isRequestFinishedRowFilterTemplate}
                header={t("Zahtev obrađen")}
                style={{ minWidth: "50px" }}
              />
            </DataTable>
          </div>
        )}
        <ConfirmDialog
          visible={deleteConfirmVisible}
          onHide={() => setDeleteConfirmVisible(false)}
          message={`${t("Da li ste sigurni da želite da obrišete zahtev")}?`}
          header={`${t("Potvrda brisanja zahteva")}`}
          icon="bi bi-exclamation-circle"
          acceptLabel={`${t("Da")}`}
          acceptClassName="accept-button"
          accept={accept}
          rejectLabel={`${t("Ne")}`}
          rejectClassName="reject-button"
          reject={() => {}}
        />
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Zahtev za obradu_")}${new Date().getTime()}`}
          />
        </div>
      </div>
    </PageLayout>
  );
};
